<template>
    <EditComponent
        dialog-id="editPhotoDialog"
        open-dialog-event="photo:edit-mode"
        updated-event="photo:updated"
        size="default"
        :confirm-function="uploadNewPhoto"
        :confirm-enabled="isButtonEnabled"
        :track-section-opened="EditionOpenedSection.PHOTO"
        :track-existing-element="!!profile?.photo"
        success-message-key="profile.picture.modal.success.message"
        success-header-key="profile.picture.modal.success.header"
        :open-dialog-post-function="loadCurrentPhoto"
        @close="clearFile"
    >
        <template #edit-header>
            <p>{{ t('profile.picture.modal.title') }}</p>
        </template>
        <template #edit-body>
            <form id="profilePhotoForm" @submit.prevent>
                <div class="picture-edit__content">
                    <div class="picture-edit__cropbox" :class="{'picture-edit__cropbox--frame': photo}">
                        <div class="picture-edit__thumbnail-wrapper">
                            <VJoyDropzone
                                v-show="!photo"
                                v-model="dropzoneFile"
                                class="picture-edit__dropzone"
                                name="dropzone"
                                :max-size="MAX_SIZE"
                                :accept="ACCEPTED_FORMATS"
                                :error-text-format="dropzoneErrorFormatMessage"
                                :error-text-max-size="dropzoneErrorMaxSizeMessage"
                            >
                                <template #dropzone-legend>
                                    <small
                                        v-show="!photo"
                                        class="picture-edit__img-rules"
                                        v-html="t('image.edit.dropzone.rules', {0: 'JPEG, PNG', 1: `${MIN_WIDTH}*${MIN_HEIGHT}`, 2: '5'})"
                                    ></small>
                                </template>
                                <template #dropzone-description>
                                    {{ t('file.dropzone.title') }}
                                </template>
                            </VJoyDropzone>

                            <img
                                v-show="!cropping"
                                class="picture-edit__thumbnail-upload"
                                alt=""
                                :src="photoUrl!"
                                :class="{'picture-edit__thumbnail-upload--loading': loading}"
                            />

                            <VueCropper
                                v-if="photoUploaded && cropping"
                                ref="cropper"
                                :src="photoUploadedUrl"
                                :check-orientation="false"
                                :aspect-ratio="1"
                                :view-mode="2"
                                :drag-mode="'crop'"
                                :container-style="{
                                    'max-height': `${CROPPER_CONTAINER_HEIGHT}px`,
                                    'max-width': `${CROPPER_CONTAINER_WIDTH}px`,
                                    margin: 'auto',
                                }"
                                :min-crop-box-width="minCropBoxWidth"
                                :min-crop-box-height="minCropBoxHeight"
                                :img-style="{height: '100%'}"
                                alt="Source Image"
                            ></VueCropper>
                        </div>
                        <div v-if="cropping" class="picture-edit__cropper-controler">
                            <VJoyIconButton
                                icon="zoom-out"
                                :label="t('profile.picture.modal.zoom.out')"
                                variant="secondary"
                                size="small"
                                @click.stop.prevent="zoom(-0.2)"
                            />
                            <VJoyButton variant="primary" size="small" icon="check" @click.stop.prevent="confirmCropping()">
                                {{ t('generic.form.validation.label') }}
                            </VJoyButton>
                            <VJoyIconButton
                                icon="zoom-in"
                                :label="t('profile.picture.modal.zoom.in')"
                                variant="secondary"
                                size="small"
                                @click.stop.prevent="zoom(0.2)"
                            />
                        </div>
                        <div v-if="photo && !cropping" class="picture-edit__cta">
                            <VJoyButton variant="secondary" icon="pencil" size="small" :disabled="loading" @click.stop.prevent="changePhoto()">
                                {{ t('profile.picture.cta.change') }}
                            </VJoyButton>

                            <VJoyButton variant="secondary" icon="web-design" size="small" :disabled="loading" @click="cropFile">
                                {{ t('profile.picture.modal.crop.image') }}
                            </VJoyButton>
                        </div>
                    </div>

                    <h1 class="picture-edit__title">{{ t('profile.picture.modal.tips.title') }}</h1>
                    <div class="picture-edit__tips-container">
                        <div v-for="tip in tips" :key="tip.message" class="picture-edit__tip">
                            <VJoyIcon size="small" :name="tip.icon" />
                            <p>{{ t(tip.message) }}</p>
                        </div>
                    </div>

                    <h1 class="picture-edit__title">{{ t('profile.picture.exemples.title') }}</h1>
                    <div class="picture-edit__examples">
                        <div v-for="examplePhotoUrl in examplePhotosUrls" :key="examplePhotoUrl" class="picture-edit__example">
                            <MaltImg
                                aria-hidden="true"
                                class="picture-edit__example-img loaded"
                                :src="examplePhotoUrl"
                                width="122"
                                quality="90"
                                format="webp"
                                alt=""
                            />
                            <VJoyIcon class="picture-edit__example-icon" name="check-circle" color="white" size="small" />
                        </div>
                    </div>
                </div>
            </form>
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {ref, watch, watchEffect, computed, onMounted} from 'vue';
    import {storeToRefs} from 'pinia';
    import {MaltImg} from '#components';
    import {useTranslation, useRoute} from '#imports';
    import VueCropper from '@malt/vue-cropperjs';
    import {pushVJoySnackbar, VJoyButton, VJoyIconButton, VJoyDropzone, VJoyIcon} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {EditionOpenedSection, ProfileUpdatedSection, EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import type {AllTranslationsRecord} from '#build/i18n';
    import EditComponent from '@/components/edit/EditComponent.vue';
    import {eventBus} from '@/config/events';
    import {useProfileStore} from '@/store/profileStore';

    const {t} = useTranslation();
    const profileStore = useProfileStore();
    const {uploadPhoto} = profileStore;
    const {profile} = storeToRefs(profileStore);

    const photo = computed(() => photoCropped.value || photoUploaded.value);
    const photoUrl = computed<string | null>(() => photoCroppedUrl.value || photoUploadedUrl.value);

    // ##### Dropzone
    const dropzoneFile = ref<File[]>([]);
    const photoUploaded = ref<File | null>(null);
    const photoUploadedUrl = computed<string | null>(() => (photoUploaded.value ? URL.createObjectURL(photoUploaded.value) : null));

    const MIN_WIDTH = 400;
    const MIN_HEIGHT = 400;
    const MAX_SIZE = 5;
    const ACCEPTED_FORMATS = ['image/jpeg', 'image/png'];
    const dropzoneErrorFormatMessage = t('general.errors.upload.file.format');
    const dropzoneErrorMaxSizeMessage = t('general.errors.upload.file.maxsize', {0: MAX_SIZE});

    watch(dropzoneFile, () => {
        const file: File | null = dropzoneFile.value ? dropzoneFile.value[0] : null;

        if (file) {
            loadPhoto(file);
        }
    });

    const currentPhotoUrl = computed(() => (profile.value?.photo?.baseUrl ? `${profile.value?.photo?.baseUrl}/${profile.value?.photo?.name}` : ''));
    async function loadCurrentPhoto(): Promise<void> {
        if (!currentPhotoUrl.value) {
            return;
        }
        const response = await fetch(currentPhotoUrl.value);
        const blob = await response.blob();
        const file = new File([blob], 'photo', {
            type: blob.type,
        });
        loadPhoto(file, true);
    }

    function loadPhoto(file: File, bypassValidation: boolean = false): void {
        const reader = new FileReader();
        reader.onload = (readerEvent: any) => {
            const base64Img = new Image();

            base64Img.onload = (img: any) => {
                if (!bypassValidation && (img.target.width < MIN_WIDTH || img.target.height < MIN_HEIGHT)) {
                    dropzoneFile.value = [];
                    pushVJoySnackbar({
                        props: {
                            message: t('image.edit.error.message.invalid.dimensions', {
                                0: img.target.width + '*' + img.target.height,
                                1: MIN_WIDTH + '*' + MIN_HEIGHT,
                            }),

                            level: 'error',
                        },
                    });
                    return;
                }

                if (!bypassValidation && !ACCEPTED_FORMATS.includes(file.type)) {
                    pushVJoySnackbar({
                        props: {
                            message: dropzoneErrorFormatMessage,
                            level: 'error',
                        },
                    });
                    return;
                }

                if (!bypassValidation && !(file.size < MAX_SIZE * 1024 * 1024)) {
                    pushVJoySnackbar({
                        props: {
                            message: dropzoneErrorMaxSizeMessage,
                            level: 'error',
                        },
                    });
                    return;
                }

                clearFile();
                photoUploaded.value = file;
            };
            base64Img.src = readerEvent.target.result;
        };
        reader.readAsDataURL(file as Blob);
    }

    function changePhoto() {
        (document.querySelector('input[name="dropzone"]') as HTMLInputElement)?.click();
    }

    // ##### Cropping
    const cropping = ref(false);
    const photoCropped = ref<Blob | null>(null);
    const photoCroppedUrl = ref<string | null>(null);
    const cropper = ref();

    function confirmCropping(): void {
        const cropConfig = cropper.value.getData({rounded: true});
        if (cropConfig.width < MIN_WIDTH || cropConfig.height < MIN_HEIGHT) {
            pushVJoySnackbar({
                props: {
                    message: t('image.edit.error.message.invalid.dimensions', {
                        0: cropConfig.width + '*' + cropConfig.height,
                        1: MIN_WIDTH + '*' + MIN_HEIGHT,
                    }),
                    level: 'error',
                },
            });

            return;
        }

        photoCroppedUrl.value = cropper.value.getCroppedCanvas().toDataURL();
        cropper.value.getCroppedCanvas().toBlob((blobValue: Blob) => {
            photoCropped.value = blobValue;
        });
        cropping.value = false;
    }

    function cropFile(): void {
        photoCroppedUrl.value = '';
        cropping.value = true;
    }

    function zoom(percent: number) {
        cropper.value.relativeZoom(percent);
    }

    // ##### Cropper crop box min size
    const CROPPER_CONTAINER_HEIGHT = 240;
    const CROPPER_CONTAINER_WIDTH = 400;
    const CROPPER_CROP_BOX_MIN_SIZE = MIN_WIDTH;
    const imageWidth = ref(0);
    const imageHeight = ref(0);
    const imgLoaded = ref(false);
    const minCropBoxWidth = computed(() => (CROPPER_CONTAINER_WIDTH * CROPPER_CROP_BOX_MIN_SIZE) / imageWidth.value);
    const minCropBoxHeight = computed(() => (CROPPER_CONTAINER_HEIGHT * CROPPER_CROP_BOX_MIN_SIZE) / imageHeight.value);

    watch(
        photoUploadedUrl,
        () => {
            if (photoUploadedUrl.value) {
                const img = new Image();
                img.onload = () => {
                    imageWidth.value = img.width;
                    imageHeight.value = img.height;
                    imgLoaded.value = true;
                };
                img.src = photoUploadedUrl.value!;
            }
        },
        {immediate: true},
    );

    // ##### Upload
    const loading = ref(false);
    const isButtonEnabled = ref<boolean>(false);
    watchEffect(() => {
        isButtonEnabled.value = photo.value !== null && !loading.value;
    });
    const {trackProfileUpdated} = useProfileTracking();

    async function uploadNewPhoto(): Promise<void | boolean> {
        if (!photo.value) {
            return;
        }
        const picture = new File([photo.value], 'blob');

        loading.value = true;

        try {
            await uploadPhoto(picture);
            trackProfileUpdated(ProfileUpdatedSection.PHOTO, !!profile.value?.photo);
            eventBus.emit('photo:updated');
            loading.value = false;
            clearFile();
        } catch (error: any) {
            if (error.statusCode === 400) {
                pushVJoySnackbar({
                    props: {
                        message: t('profile.photo.error.generic.message'),
                        level: 'error',
                    },
                });
            }

            pushVJoySnackbar({
                props: {
                    message: t('generic.error.unexpected'),
                    level: 'error',
                },
            });
            loading.value = false;
            return false;
        }
    }

    // ##### Clear file
    function clearFile(): void {
        photoUploaded.value = null;
        cropping.value = false;
        photoCropped.value = null;
        photoCroppedUrl.value = null;
        loading.value = false;
        dropzoneFile.value = [];
    }

    // ##### Tips and examples
    const tips: {icon: JoyIconsId; message: keyof AllTranslationsRecord}[] = [
        {
            icon: 'mood-good',
            message: 'profile.picture.modal.tips.no.sunglasses',
        },
        {
            icon: 'user-lighter',
            message: 'profile.picture.modal.tips.one.face.only',
        },
        {
            icon: 'camera',
            message: 'profile.picture.modal.tips.face.camera',
        },
    ];
    const examplePhotosUrls: string[] = ['/profile/ProfilePictureSample001', '/profile/ProfilePictureSample002', '/profile/ProfilePictureSample003'];

    // ##### Auto-open drawer
    const route = useRoute();
    onMounted(() => {
        if ('edit-photo' in route.query) {
            eventBus.emit('photo:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
        }
    });
</script>
<style lang="scss">
    @import 'cropperjs/dist/cropper.min.css';
</style>
<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .picture-edit {
        &__cropbox {
            display: flex;
            flex-direction: column;
            margin-bottom: var(--joy-core-spacing-8);

            &--frame {
                background-color: var(--joy-color-neutral-10);
                border-radius: var(--joy-core-radius-4);
                border: 1px solid var(--joy-color-neutral-30);
                padding-top: var(--joy-core-spacing-6);
                padding-bottom: var(--joy-core-spacing-6);
            }
        }

        &__dropzone {
            :deep(.joy-dropzone__instruction) {
                display: flex;
                justify-content: center;
                height: 190px;
            }
        }

        &__cta {
            width: 100%;
            display: flex;
            justify-content: center;
            gap: var(--joy-core-spacing-4);
            padding: 0 var(--joy-core-spacing-6);
        }

        &__cropper-controler {
            display: flex;
            justify-content: center;
            gap: var(--joy-core-spacing-4);
            margin-top: var(--joy-core-spacing-6);
        }

        &__title {
            font-size: var(--joy-font-size-primary-400);
            font-weight: var(--joy-font-weight-bold);
            color: var(--joy-color-text-default);
            margin-bottom: var(--joy-core-spacing-6);
        }

        &__tips-container {
            display: flex;
            justify-content: center;
            gap: var(--joy-core-spacing-8);
            margin-bottom: var(--joy-core-spacing-6);

            @include joy.screen_xxs {
                gap: var(--joy-core-spacing-4);
            }
        }

        &__tip {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: var(--joy-core-spacing-2);
            margin-bottom: var(--joy-core-spacing-3);
            font-size: var(--joy-font-size-primary-300);
            text-align: center;
        }

        &__examples {
            display: flex;
            justify-content: space-between;
        }

        &__example {
            position: relative;
        }

        &__example-img {
            border-radius: var(--joy-core-radius-4);

            @include joy.screen_xxs {
                width: 90px;
            }
        }

        &__example-icon {
            position: absolute;
            bottom: var(--joy-core-spacing-2);
            right: var(--joy-core-spacing-2);
        }

        &__thumbnail-wrapper {
            width: 100%;
            display: flex;
            justify-content: center;
            flex-flow: column;

            > button {
                margin-right: 5px;
            }
        }

        &__img-rules {
            margin-top: var(--joy-core-spacing-2);
            color: var(--joy-color-neutral-40);
        }

        &__thumbnail-upload {
            max-height: 240px;
            margin-bottom: var(--joy-core-spacing-6);
            margin-left: auto;
            margin-right: auto;
            display: block;
            border-radius: var(--joy-core-radius-4);

            &--loading {
                opacity: 0.4;
            }
        }
    }
</style>
